import { WarningIcon } from "@chakra-ui/icons";
import { Box, Flex, Image, Text } from "@chakra-ui/react";
import QRCode from "qrcode";
import { FC, useEffect, useState } from "react";
import { useSnapshot } from "valtio";
import { userStore } from "../../../../hooks/store";
import { editorState } from "../../../../utils/editor";
import { isValidISODate } from "../../../../utils/glob";
import { getContrastRatio, getLuminance } from "../../utils";
import { QRPosition } from "../../../../types/wedding";

type AccessCardSampleProps = {
  hideEditButton?: boolean;
};

const getQRPositionStyles = (position: QRPosition = "center") => {
  const baseStyles = {
    position: "absolute" as const,
    zIndex: 10,
  };

  switch (position) {
    case "top-left":
      return { ...baseStyles, top: "1rem", left: "1rem" };
    case "top-right":
      return { ...baseStyles, top: "1rem", right: "1rem" };
    case "bottom-left":
      return { ...baseStyles, bottom: "6rem", left: "1rem" };
    case "bottom-right":
      return { ...baseStyles, bottom: "6rem", right: "1rem" };
    case "center":
    case "custom":
    default:
      return {};
  }
};

const AccessCardSample: FC<AccessCardSampleProps> = ({ hideEditButton }) => {
  const [contrastOk, setContrastOk] = useState(true);

  const { groomFirstName, brideFirstName, accessCards, mainContent } =
    useSnapshot(editorState);

  const { data: loggedInUserData } = useSnapshot(userStore);
  const loggedInUserFirstName = loggedInUserData.firstname;
  const loggedInUserLastName = loggedInUserData.lastname;

  const backgroundCtx = accessCards?.backgroundCtx || "color";
  const backgroundColor = accessCards?.backgroundColor?.colorCode || "#ffffff";
  const textColor = accessCards?.textColor?.colorCode || "#000000";
  const backgroundImage = accessCards?.backgroundImage;
  const address = accessCards?.address || "";
  const qrSize = accessCards?.qrSize ?? 100;

  let date = new Date(accessCards?.date);
  date = isValidISODate(date?.toISOString?.()) ? date : mainContent.date;
  const options = { year: "numeric", month: "long", day: "numeric" };
  const formattedDate = date.toLocaleDateString(
    "en-US",
    options as Intl.DateTimeFormatOptions
  );

  const [qrCodeURL, setQrCodeURL] = useState("");

  useEffect(() => {
    const generateQRCode = async () => {
      try {
        const qrDataUrl = await QRCode.toDataURL("weddn.co", {
          color: { dark: textColor, light: "#0000" },
        });
        setQrCodeURL(qrDataUrl);
      } catch (err) {
        console.error("Error generating QR code:", err);
      }
    };

    generateQRCode();
  }, [textColor]);

  useEffect(() => {
    const checkContrast = () => {
      let contrastOkValue = true;
      const OVERLAY_LUMINANCE = 0.03;

      if (backgroundCtx === "color") {
        const contrastRatio = getContrastRatio(textColor, backgroundColor);
        contrastOkValue = contrastRatio >= 4.5;
      } else if (backgroundCtx === "image") {
        const textLum = getLuminance(textColor);
        const contrastRatio = (textLum + 0.05) / (OVERLAY_LUMINANCE + 0.05);
        contrastOkValue = contrastRatio >= 4.5;
      }

      setContrastOk(contrastOkValue);
    };

    checkContrast();
  }, [textColor, backgroundColor, backgroundCtx]);

  return (
    <Box
      w={"full"}
      h={"full"}
      maxW={"398px"}
      maxH={"600px"}
      minH={"450px"}
      borderRadius={"8px"}
      order={1} // Don't touch this
    >
      <Box
        {...(backgroundCtx === "image"
          ? {
              bgImage: backgroundImage,
              bgSize: "cover",
            }
          : { bgColor: backgroundColor })}
        bgSize={"cover"}
        bgPos={"center"}
        w={"full"}
        h={"inherit"}
        minH={"inherit"}
        maxH={"inherit"}
        pos={"relative"}
        borderRadius="md"
        textAlign="center"
        overflow={"hidden"}
        p={5}
      >
        {backgroundCtx === "image" && (
          <Box
            pos={"absolute"}
            w={"100%"}
            top={0}
            bottom={0}
            right={0}
            left={0}
            h={"100%"}
            borderRadius="md"
            bg={`rgba(0, 0, 0, ${accessCards?.overlayOpacity ?? 0.7})`}
            zIndex={2}
            boxShadow={"inset 0px -70px 30px rgba(0, 0, 0, 0.5)"}
          ></Box>
        )}
        {!contrastOk && (
          <Flex
            align="center"
            justify="center"
            gap="0.35rem"
            pos="absolute"
            top="0.5rem"
            left="0.5rem"
            zIndex={10}
            bg="orange.400"
            color="white"
            px="0.5rem"
            py="0.25rem"
            borderRadius="md"
            fontSize="xs"
            fontWeight="bold"
            boxShadow="sm"
            lineHeight="1"
          >
            <WarningIcon boxSize="0.85rem" mb="1px" />
            <Text>Low Contrast</Text>
          </Flex>
        )}
        <Flex
          justify={"center"}
          align={"center"}
          w={"4rem"}
          h={"4rem"}
          bg={"white"}
          borderRadius={"50%"}
          pos={"absolute"}
          top={"0.5rem"}
          right={"0.5rem"}
          zIndex={5}
        >
          <Text
            color={"black"}
            fontSize={"14px"}
            lineHeight={"12px"}
            fontWeight={600}
          >
            admits one
          </Text>
        </Flex>
        <Flex
          flexDir={"column"}
          alignItems={"center"}
          justifyContent={"space-evenly"}
          h={"100%"}
          pb={"1rem"}
          pos={"relative"}
          zIndex={2}
        >
          <Flex flexDir={"column"} gap={"0.5rem"}>
            {accessCards?.visibility?.showWeddingOfLabel !== false && (
              <Text
                fontSize={"0.765rem"}
                fontWeight={600}
                letterSpacing={"1px"}
                textTransform={"uppercase"}
                opacity={0.8}
                zIndex={2}
                color={textColor}
              >
                The wedding of
              </Text>
            )}
            {accessCards?.visibility?.showCoupleNames !== false && (
              <Text
                fontSize={"2.5rem"}
                lineHeight={"2.5rem"}
                fontWeight={500}
                fontFamily={"Gilda"}
                mt={{ base: 0, md: "-0.675rem" }}
                zIndex={2}
                color={textColor}
              >
                {brideFirstName} & {groomFirstName}
              </Text>
            )}
          </Flex>
          {qrCodeURL &&
            (!accessCards?.qrPosition ||
              accessCards.qrPosition === "center") && (
              <Image
                src={qrCodeURL}
                w={`${qrSize + 80}px`}
                h={`${qrSize + 80}px`}
              />
            )}
          {accessCards?.visibility?.showGuestName !== false && (
            <Text
              fontSize={"1.6rem"}
              lineHeight={"30.24px"}
              fontWeight={500}
              letterSpacing={"1px"}
              zIndex={2}
              color={textColor}
              mb={"2rem"}
            >
              {loggedInUserFirstName} {loggedInUserLastName}
            </Text>
          )}
          {accessCards?.visibility?.showDate !== false &&
            !accessCards.hideDetails && (
              <Text
                fontSize={"15px"}
                lineHeight={"20.16px"}
                zIndex={2}
                color={textColor}
              >
                {formattedDate}
              </Text>
            )}
          {accessCards?.visibility?.showVenue !== false &&
            !accessCards.hideDetails &&
            address && (
              <Text
                fontSize={"17px"}
                lineHeight={"20.16px"}
                mt={{ base: 0, md: "-10px" }}
                zIndex={2}
                color={textColor}
                dangerouslySetInnerHTML={{ __html: address }}
              />
            )}
        </Flex>
        {qrCodeURL && accessCards?.qrPosition === "custom" && (
          <Image
            src={qrCodeURL}
            w={`${qrSize}px`}
            h={`${qrSize}px`}
            pos={"absolute"}
            zIndex={10}
            left={`calc(${accessCards?.qrCustomPosition?.x ?? 50}% - ${
              qrSize / 2
            }px + ${
              (accessCards?.qrCustomPosition?.x ?? 50) < 50
                ? "20px"
                : (accessCards?.qrCustomPosition?.x ?? 50) > 50
                ? "-20px"
                : "0px"
            })`}
            top={`calc(${accessCards?.qrCustomPosition?.y ?? 50}% - ${
              qrSize / 2
            }px + ${
              (accessCards?.qrCustomPosition?.y ?? 50) < 50
                ? "20px"
                : (accessCards?.qrCustomPosition?.y ?? 50) > 50
                ? "-20px"
                : "0px"
            })`}
          />
        )}
        {qrCodeURL &&
          accessCards?.qrPosition &&
          accessCards.qrPosition !== "center" &&
          accessCards.qrPosition !== "custom" && (
            <Image
              src={qrCodeURL}
              w={`${qrSize}px`}
              h={`${qrSize}px`}
              {...getQRPositionStyles(accessCards.qrPosition)}
            />
          )}
      </Box>
      <Box
        display={hideEditButton ? "block" : "none"}
        bg={backgroundColor}
        w={"full"}
        h={"40px"}
      ></Box>
    </Box>
  );
};

export default AccessCardSample;
